import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Grid, Icon, Dropdown, Checkbox, Menu } from 'semantic-ui-react';
import { isMobileOnly } from 'mobile-device-detect';
import { useTextResources } from '../../language';
import HotelsDropDown from '../../components/shared/HotelsDropDown';
import { ChainAddonsDropDown } from '../../components/shared/ChainAddonsDropDown';
import { DatePicker } from '../../components/shared/DatePicker';
import { AvailabilityMax, AvailabilityMid, AvailabilityMin } from './constants.js';

/**
 * HotelAddonsToolbar component for controlling calendar navigation and view settings.
 * Follows the v6 pattern from Calendar/toolbar.jsx
 */
const HotelAddonsToolbar = ({
    title,
    defaultView,
    onPrev,
    onNext,
    onChangeView,
    onSetDate,
    viewSettings,
    onToggleSetting,
    onChangeAvailabilityView,
    hotels,
    addons,
    setSelectedHotelIds,
    setSelectedChainAddonIds,
}) => {
    const text = useTextResources();
    const isHebrew = window.user?.UserSettings?.[0]?.Language?.Code?.startsWith('he');

    const viewOptions = useMemo(() => [
        { value: 'resourceTimelineDay', key: 'day', text: text('Day') },
        { value: 'resourceTimelineWeek', key: 'week', text: text('Week') },
        { value: 'resourceTimelineBiWeek', key: 'biweek', text: text('TwoWeeks') },
        { value: 'resourceTimelineSlidingMonth', key: 'current', text: text('Month') },
        { value: 'resourceTimelineMonth', key: 'month', text: text('CurrentMonth') },
    ], [text]);

    const availabilityViewOptions = useMemo(() => [
        {
            key: 'availabilityMax',
            text: (
                <Checkbox
                    checked={viewSettings.selectedAvailabilityView === AvailabilityMax}
                    label={text('AddonsAvailabilityViewMax')}
                    onChange={(_e, { checked }) => onChangeAvailabilityView(AvailabilityMax, checked)}
                />
            ),
        },
        {
            key: 'availabilityMid',
            text: (
                <Checkbox
                    checked={viewSettings.selectedAvailabilityView === AvailabilityMid}
                    label={text('AddonsAvailabilityViewMid')}
                    onChange={(_e, { checked }) => onChangeAvailabilityView(AvailabilityMid, checked)}
                />
            ),
        },
        {
            key: 'availabilityMin',
            text: (
                <Checkbox
                    checked={viewSettings.selectedAvailabilityView === AvailabilityMin}
                    label={text('AddonsAvailabilityViewMin')}
                    onChange={(_e, { checked }) => onChangeAvailabilityView(AvailabilityMin, checked)}
                />
            ),
        },
    ], [text, viewSettings.selectedAvailabilityView, onChangeAvailabilityView]);

    const dataViewOptions = useMemo(() => [
        {
            key: 'showAvailable',
            text: (
                <Checkbox
                    checked={viewSettings.showAvailable}
                    label={text('AddonsDataAvailable')}
                    onChange={() => onToggleSetting('showAvailable')}
                />
            ),
        },
        {
            key: 'showSold',
            text: (
                <Checkbox
                    checked={viewSettings.showSold}
                    label={text('AddonsDataSold')}
                    onChange={() => onToggleSetting('showSold')}
                />
            ),
        },
        {
            key: 'showInventory',
            text: (
                <Checkbox
                    checked={viewSettings.showInventory}
                    label={text('AddonsDataInventory')}
                    onChange={() => onToggleSetting('showInventory')}
                />
            ),
        },
    ], [text, viewSettings.showAvailable, viewSettings.showSold, viewSettings.showInventory, onToggleSetting]);

    const settingsViewOptions = useMemo(() => [
        {
            key: 'showSoldAddons',
            text: (
                <Checkbox
                    checked={viewSettings.showSoldAddons}
                    label={text('AddonsSettingsSoldAddons')}
                    onChange={() => onToggleSetting('showSoldAddons')}
                />
            ),
        },
        {
            key: 'showGrossInventoryDiff',
            text: (
                <Checkbox
                    checked={viewSettings.showGrossInventoryDiff}
                    label={text('AddonsSettingsGrossInventory')}
                    onChange={() => onToggleSetting('showGrossInventoryDiff')}
                />
            ),
        },
    ], [text, viewSettings.showSoldAddons, viewSettings.showGrossInventoryDiff, onToggleSetting]);

    if (isMobileOnly) {
        return (
            <Grid columns="1" verticalAlign="middle" style={{ marginBottom: '.5rem' }}>
                <Grid.Row className="mobile-date-title" columns={1} textAlign="center">
                    <Grid.Column textAlign="center" className={`date-title ${isHebrew ? 'title-rtl' : 'title-ltr'}`}>
                        <Icon name="arrow left" link onClick={onPrev} fitted />
                        <DatePicker
                            className="calendar-datepicker"
                            id="dateinput"
                            type="basic"
                            label={title}
                            onChange={onSetDate}
                        />
                        <Icon name="arrow right" link onClick={onNext} fitted />
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row className="calendar-setting-mobile">
                    <Grid.Column textAlign="center">
                        <Menu secondary compact size="tiny">
                            <Dropdown
                                item
                                value={defaultView}
                                options={viewOptions}
                                onChange={(e, data) => onChangeView(data.value)}
                            />
                            <Dropdown
                                direction="left"
                                item
                                text={text('AddonsAvailabilityViewChange')}
                                value=""
                                options={availabilityViewOptions}
                            />
                            <Dropdown
                                direction="left"
                                item
                                text={text('AddonsDataViewChange')}
                                value=""
                                options={dataViewOptions}
                            />
                            <Dropdown
                                direction="left"
                                item
                                text={text('AddonsSettingsViewChange')}
                                value=""
                                options={settingsViewOptions}
                            />
                        </Menu>
                    </Grid.Column>
                </Grid.Row>
            </Grid>
        );
    }

    return (
        <Grid columns="3" verticalAlign="middle">
            <Grid.Row style={{ marginBottom: '.5rem' }}>
                <Grid.Column>
                    <div className="filter-container">
                        <HotelsDropDown
                            label={text('Hotels')}
                            multiple
                            onChange={setSelectedHotelIds}
                            data={hotels}
                            placeholder={text('All')}
                            search
                            clearable
                            style={{ minWidth: '130px' }}
                        />
                        <ChainAddonsDropDown
                            label={text('Addon')}
                            multiple
                            onChange={setSelectedChainAddonIds}
                            data={addons}
                            placeholder={text('All')}
                            search
                            clearable
                            style={{ minWidth: '130px' }}
                        />
                    </div>
                </Grid.Column>
                <Grid.Column textAlign="center" className={`date-title ${isHebrew ? 'title-rtl' : 'title-ltr'}`}>
                    <Icon name="arrow left" link onClick={onPrev} fitted />
                    <DatePicker
                        className="calendar-datepicker"
                        id="dateinput"
                        type="basic"
                        label={title}
                        onChange={onSetDate}
                    />
                    <Icon name="arrow right" link onClick={onNext} fitted />
                </Grid.Column>
                <Grid.Column textAlign="right">
                    <Menu secondary compact>
                        <Dropdown
                            item
                            value={defaultView}
                            options={viewOptions}
                            onChange={(e, data) => onChangeView(data.value)}
                        />
                        <Dropdown
                            direction="left"
                            item
                            text={text('AddonsAvailabilityViewChange')}
                            value=""
                            options={availabilityViewOptions}
                        />
                        <Dropdown
                            direction="left"
                            item
                            text={text('AddonsDataViewChange')}
                            value=""
                            options={dataViewOptions}
                        />
                        <Dropdown
                            direction="left"
                            item
                            text={text('AddonsSettingsViewChange')}
                            value=""
                            options={settingsViewOptions}
                        />
                    </Menu>
                </Grid.Column>
            </Grid.Row>
        </Grid>
    );
};

HotelAddonsToolbar.propTypes = {
    title: PropTypes.string,
    defaultView: PropTypes.string,
    onPrev: PropTypes.func,
    onNext: PropTypes.func,
    onChangeView: PropTypes.func,
    onSetDate: PropTypes.func,
    viewSettings: PropTypes.object,
    onToggleSetting: PropTypes.func,
    onChangeAvailabilityView: PropTypes.func,
    hotels: PropTypes.array,
    addons: PropTypes.array,
    setSelectedHotelIds: PropTypes.func,
    setSelectedChainAddonIds: PropTypes.func,
};

export default HotelAddonsToolbar;
